import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { getRequest } from '../../../../../Helper/ApiClient';
import type { ISampleProductSize } from '../../../Abstracts/ExhibitorPopup/Samples/Samples';
import type { IVtsBasketSamplePosition } from '../../../Abstracts/ExhibitorPopup/Samples/SampleProductList/SampleProductList';
import type { BoothPanelProps } from '../panels';
import { VTS_BASKET_QUERY_KEY } from '../AssortmentPanel/useBoothProducts';
import SampleRow from './SampleRow/SampleRow';
import styles from './SamplesPanel.module.scss?module';

/**
 * GET /api/onlineshop/vts-exhibitors/{exhibitorId}/user-sample-limits. The endpoint answers
 * null when no limit is configured for the user's ABC class.
 */
interface IVtsUserSampleLimits {
  total_samples_limit: number;
  exhibitor_samples_limit: number;
  exhibitor_samples_quantity: number;
}

/** Limit and stock errors arrive as HTTP 200 with an error text; success is null. */
interface IVtsSampleAddResponse {
  error?: string;
}

const NUMBER_FORMAT = new Intl.NumberFormat('en-GB');
/** How long "Added" stays on the button. */
const ADDED_RESET_MS = 2500;
const SKELETON_ROWS = 3;
const ADD_FAILED_MESSAGE = 'Could not add the samples. Please try again.';

async function postSamplePositions(
  positions: IVtsBasketSamplePosition[]
): Promise<IVtsSampleAddResponse | null> {
  const formData = new FormData();
  formData.append('positionsJson', JSON.stringify(positions));
  const response = await axios.post<IVtsSampleAddResponse | null>(
    '/api/onlineshop/vts-basket-add-sample-position',
    formData,
    {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    }
  );

  return response.data;
}

/**
 * Samples: the user's allowance with this brand and the sample flavours on offer. Limit and
 * stock errors of the add endpoint are shown under the button.
 */
export default function SamplesPanel({
  exhibitor,
  tab,
  onBadge,
  track,
}: BoothPanelProps) {
  const queryClient = useQueryClient();
  const samplesRequest = getRequest<ISampleProductSize[]>(
    '/api/onlineshop/vts-exhibitors/' + exhibitor.id + '/samples'
  );
  const limitsRequest = getRequest<IVtsUserSampleLimits | null>(
    '/api/onlineshop/vts-exhibitors/' + exhibitor.id + '/user-sample-limits'
  );
  const addMutation = useMutation({ mutationFn: postSamplePositions });
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [added, setAdded] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const limits = limitsRequest.data ?? null;
  const badge = limits
    ? Math.max(
        0,
        limits.exhibitor_samples_limit - limits.exhibitor_samples_quantity
      )
    : null;

  useEffect(() => {
    onBadge(tab, badge);
  }, [tab, onBadge, badge]);

  useEffect(() => {
    return () => {
      if (resetTimer.current !== null) {
        clearTimeout(resetTimer.current);
      }
    };
  }, []);

  const sampleSizes = (samplesRequest.data ?? []).filter(
    (size) => size.sample_product_attributes.length > 0
  );
  const positions: IVtsBasketSamplePosition[] = Object.entries(quantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([id, quantity]) => ({
      sample_product_attribute_id: Number(id),
      quantity,
    }));
  const selectedUnits = positions.reduce(
    (sum, position) => sum + position.quantity,
    0
  );

  const handleQuantityChange = (attributeId: number, quantity: number) => {
    setQuantities((prev) => ({ ...prev, [attributeId]: quantity }));
    setErrorMessage(null);
  };

  const handleAdd = () => {
    if (selectedUnits === 0) {
      return;
    }
    const units = selectedUnits;
    setErrorMessage(null);
    addMutation.mutate(positions, {
      onSuccess: (data) => {
        if (data && typeof data.error === 'string' && data.error !== '') {
          setErrorMessage(data.error);
          return;
        }
        setQuantities({});
        setAdded(true);
        track('sample_added', { units });
        limitsRequest.refetch();
        samplesRequest.refetch();
        queryClient.invalidateQueries({ queryKey: VTS_BASKET_QUERY_KEY });
        if (resetTimer.current !== null) {
          clearTimeout(resetTimer.current);
        }
        resetTimer.current = setTimeout(() => {
          resetTimer.current = null;
          setAdded(false);
        }, ADDED_RESET_MS);
      },
      onError: () => {
        setErrorMessage(ADD_FAILED_MESSAGE);
      },
    });
  };

  return (
    <div className={styles.panel}>
      {limits && (
        <section className={styles.allowanceCard}>
          <h2 className={styles.sectionLabel}>
            Your allowance with {exhibitor.name}
          </h2>
          <p className={styles.allowanceText}>
            <strong>
              {NUMBER_FORMAT.format(limits.exhibitor_samples_quantity)} of{' '}
              {NUMBER_FORMAT.format(limits.exhibitor_samples_limit)} samples
              chosen
            </strong>{' '}
            · {NUMBER_FORMAT.format(limits.total_samples_limit)} in total across
            all booths
          </p>
          <div
            className={styles.progress}
            role="progressbar"
            aria-label={'Samples chosen with ' + exhibitor.name}
            aria-valuemin={0}
            aria-valuemax={limits.exhibitor_samples_limit}
            aria-valuenow={Math.min(
              limits.exhibitor_samples_quantity,
              limits.exhibitor_samples_limit
            )}
          >
            <span
              className={styles.progressFill}
              style={{ width: progressPercent(limits) + '%' }}
            />
          </div>
        </section>
      )}
      <p className={styles.hint}>
        Sample stock is limited. You find all chosen samples in your order
        overview.
      </p>

      <section className={styles.section} aria-busy={samplesRequest.isPending}>
        <h2 className={styles.sectionLabel}>Choose samples</h2>
        {samplesRequest.isPending ? (
          <ul className={styles.sampleList} aria-label="Loading samples">
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <li key={index} className={styles.skeletonRow} />
            ))}
          </ul>
        ) : samplesRequest.isError ? (
          <p className={styles.message} role="alert">
            Samples could not be loaded. Please try again later.
          </p>
        ) : sampleSizes.length === 0 ? (
          <p className={styles.message}>No samples offered at this booth.</p>
        ) : (
          <>
            <ul className={styles.sampleList}>
              {sampleSizes.map((size) => (
                <li key={size.id} className={styles.sampleGroup}>
                  <h3 className={styles.sampleSizeName}>{size.name}</h3>
                  <ul className={styles.sampleFlavours}>
                    {size.sample_product_attributes.map((attribute) => (
                      <SampleRow
                        key={attribute.id}
                        attribute={attribute}
                        sizeName={size.name}
                        quantity={quantities[attribute.id] ?? 0}
                        onQuantityChange={handleQuantityChange}
                      />
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
            <div className={styles.footer}>
              <div className={styles.footerActions}>
                <p className={styles.selectedCount} aria-live="polite">
                  {NUMBER_FORMAT.format(selectedUnits)}{' '}
                  {selectedUnits === 1 ? 'sample' : 'samples'} selected
                </p>
                <button
                  type="button"
                  className={styles.primaryButton}
                  disabled={selectedUnits === 0 || addMutation.isPending}
                  onClick={handleAdd}
                >
                  {added ? (
                    <>
                      <FontAwesomeIcon icon={faCheck} /> Added
                    </>
                  ) : (
                    'Add samples'
                  )}
                </button>
              </div>
              <p className={styles.visuallyHidden} role="status">
                {added ? 'Samples added to your order' : ''}
              </p>
              {errorMessage && (
                <p className={styles.errorText} role="alert">
                  {errorMessage}
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function progressPercent(limits: IVtsUserSampleLimits): number {
  if (limits.exhibitor_samples_limit <= 0) {
    return 0;
  }

  return Math.min(
    100,
    (limits.exhibitor_samples_quantity / limits.exhibitor_samples_limit) * 100
  );
}
